import {
  type ChangeEvent,
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { isToday } from 'date-fns'
import {
  type CollectingFormValues,
  type CollectingTransactionRow,
  type ReturningFormValues,
  type ReturningTransactionRow,
} from '../types/transactions'
import {
  getStoredSelectedBusinessAssociationForUser,
  setStoredSelectedBusinessAssociationForUser,
} from '../utils/businessAssociationStorage'
import {
  addTransactionRow,
  createTransactionRow,
  isTransactionRowFilled as isSharedTransactionRowFilled,
  removeTransactionRow,
  updateTransactionRow,
} from '../utils/transactionRowUtils'

export type BusinessAssociation = {
  id: number
  name: string
  role: string
}

type SharedFormValues = CollectingFormValues | ReturningFormValues
type SharedTransactionRow = CollectingTransactionRow | ReturningTransactionRow

export const isTransactionRowFilled = isSharedTransactionRowFilled

export type TransactionFormContextValue = {
  values: SharedFormValues
  isSubmitting: boolean
  setIsSubmitting: (isSubmitting: boolean) => void
  setUserName: (userName: string | undefined) => void
  setCustomerNumber: (customerNumber: number) => void
  setHub: (hub: string) => void
  setNote: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  setTransactionType: (transactionType: string) => void
  setTransactionDate: (transactionDate: Date | null) => void
  setDepot: (depot: string, depotLabel?: string) => void
  addTransactionRow: () => string
  removeTransactionRow: (id: string) => void
  updateTransactionRow: (
    id: string,
    updates: Partial<Omit<SharedTransactionRow, 'id'>>
  ) => void
  selectedBusinessAssociation: BusinessAssociation | null
  setSelectedBusinessAssociation: (
    association: BusinessAssociation | null
  ) => void
  isBusinessAssociationSelectionReady: boolean
  setBusinessAssociationSelectionReady: (ready: boolean) => void
  isEditMode: boolean
  editingTransactionId: string | null
  editingAssociationId: number | null
  initializeForEdit: (values: SharedFormValues, transactionId: string) => void
  resetToCreate: () => void
  spendingLimitShownKey: string | null
  setSpendingLimitShownKey: (key: string | null) => void
}

export type TransactionFormContextValueExtended =
  TransactionFormContextValue & {
    isStep1Complete: boolean
    isStep2Complete: boolean
    setLocationManagingCheckPassed: (passed: boolean) => void
  }

const normalizeSharedFormValues = (
  values: SharedFormValues
): SharedFormValues => {
  const transactionDate =
    values.transactionDate ?? values.collectingDate ?? null

  return {
    ...values,
    transactionDate,
    collectingDate: transactionDate,
  }
}

const createInitialFlowValues = (): SharedFormValues => ({
  userName: '',
  customerNumber: undefined,
  hub: '',
  transactionType: '',
  depot: '',
  transactionDate: new Date(),
  collectingDate: new Date(),
  transactions: [createTransactionRow()],
})

const initialFlowValues = createInitialFlowValues()

const TransactionFormContext =
  createContext<TransactionFormContextValueExtended | null>(null)

type TransactionFormProviderProps = PropsWithChildren<{
  initialValues?: SharedFormValues
}>

export const TransactionFormProvider = ({
  children,
  initialValues,
}: TransactionFormProviderProps) => {
  const [values, setValues] = useState<SharedFormValues>(
    initialValues ? normalizeSharedFormValues(initialValues) : initialFlowValues
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingTransactionId, setEditingTransactionId] = useState<
    string | null
  >(null)
  const [editingAssociationId, setEditingAssociationId] = useState<
    number | null
  >(null)
  const [selectedBusinessAssociation, setSelectedBusinessAssociationState] =
    useState<BusinessAssociation | null>(null)
  const [
    isBusinessAssociationSelectionReady,
    setBusinessAssociationSelectionReady,
  ] = useState(false)
  const [spendingLimitShownKey, setSpendingLimitShownKey] = useState<
    string | null
  >(null)
  const [locationManagingCheckPassed, setLocationManagingCheckPassed] =
    useState(true)

  const setSelectedBusinessAssociation = useCallback(
    (association: BusinessAssociation | null) => {
      if (selectedBusinessAssociation?.id !== association?.id) {
        setSpendingLimitShownKey(null)
      }

      setSelectedBusinessAssociationState(association)

      // Always persist: this is an explicit user action, and skipping the write
      // during edit mode has no compensating write afterwards, so it would be lost.
      setStoredSelectedBusinessAssociationForUser({
        userName: values.userName,
        association,
      })
    },
    [selectedBusinessAssociation?.id, values.userName]
  )

  const initializeForEdit = useCallback(
    (editValues: SharedFormValues, transactionId: string) => {
      setValues(normalizeSharedFormValues(editValues))
      setEditingTransactionId(transactionId)
      setEditingAssociationId(selectedBusinessAssociation?.id ?? null)
      setIsEditMode(true)
    },
    [selectedBusinessAssociation?.id]
  )

  const resetToCreate = useCallback(() => {
    // Preserve identity fields: storage writes are keyed on userName and are
    // silently skipped when it's empty, so wiping it here would break
    // persistence for every business-association change afterwards.
    setValues(current => ({
      ...createInitialFlowValues(),
      userName: current.userName,
      customerNumber: current.customerNumber,
    }))
    setEditingTransactionId(null)
    setEditingAssociationId(null)
    setIsEditMode(false)
  }, [])

  const contextValue = useMemo<TransactionFormContextValueExtended>(() => {
    const isStep1Complete =
      isEditMode ||
      Boolean(
        values.hub &&
        values.hub !== '' &&
        values.transactionType &&
        values.transactionType !== '' &&
        locationManagingCheckPassed
      )

    const isStep2Complete =
      values.transactions.length > 0 &&
      values.transactions.every(isTransactionRowFilled)

    return {
      values,
      isSubmitting,
      setIsSubmitting,
      setHub: (hub: string) => {
        setValues(current =>
          current.hub === hub
            ? current
            : {
                ...current,
                hub,
                transactionType: '',
                depot: '',
                depotLabel: undefined,
              }
        )
      },
      setUserName: (userName: string | undefined) => {
        setValues(current => {
          if (current.userName === userName) {
            return current
          }

          setBusinessAssociationSelectionReady(false)
          return { ...current, userName }
        })
        setSelectedBusinessAssociationState(
          getStoredSelectedBusinessAssociationForUser(userName)
        )
      },
      setCustomerNumber: (customerNumber: number) => {
        setValues(current => ({ ...current, customerNumber }))
      },
      setNote: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setValues(current => ({ ...current, note: e.target.value }))
      },
      setTransactionType: (transactionType: string) => {
        setValues(current => ({ ...current, transactionType }))
      },
      setTransactionDate: (selectedDate: Date | null) => {
        // The date picker returns midnight; for today keep the current time.
        const transactionDate =
          selectedDate && isToday(selectedDate) ? new Date() : selectedDate

        setValues(current => ({
          ...current,
          transactionDate,
          collectingDate: transactionDate,
        }))
      },
      setDepot: (depot: string, depotLabel?: string) => {
        setValues(current => ({ ...current, depot, depotLabel }))
      },
      addTransactionRow: () => {
        let newRowId = ''
        setValues(current => {
          const result = addTransactionRow(current.transactions)
          newRowId = result.newRowId

          return {
            ...current,
            transactions: result.transactions,
          }
        })
        return newRowId
      },
      removeTransactionRow: (id: string) => {
        setValues(current => ({
          ...current,
          transactions: removeTransactionRow({
            transactions: current.transactions,
            id,
          }),
        }))
      },
      updateTransactionRow: (
        id: string,
        updates: Partial<Omit<SharedTransactionRow, 'id'>>
      ) => {
        setValues(current => ({
          ...current,
          transactions: updateTransactionRow({
            transactions: current.transactions,
            id,
            updates,
          }),
        }))
      },
      isStep1Complete,
      isStep2Complete,
      setLocationManagingCheckPassed,
      selectedBusinessAssociation,
      setSelectedBusinessAssociation,
      isBusinessAssociationSelectionReady,
      setBusinessAssociationSelectionReady,
      isEditMode,
      editingTransactionId,
      editingAssociationId,
      initializeForEdit,
      resetToCreate,
      spendingLimitShownKey,
      setSpendingLimitShownKey,
    }
  }, [
    isSubmitting,
    values,
    selectedBusinessAssociation,
    setSelectedBusinessAssociation,
    isBusinessAssociationSelectionReady,
    isEditMode,
    editingTransactionId,
    editingAssociationId,
    initializeForEdit,
    resetToCreate,
    spendingLimitShownKey,
    locationManagingCheckPassed,
  ])

  return (
    <TransactionFormContext.Provider value={contextValue}>
      {children}
    </TransactionFormContext.Provider>
  )
}

export const useTransactionFormContextCore = (errorMessage: string) => {
  const context = useContext(TransactionFormContext)

  if (!context) {
    throw new Error(errorMessage)
  }

  return context
}
